import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { ArrowRight, ArrowLeft, Loader2, Sparkles, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STEPS = [
  {
    key: "business_type",
    title: "Che tipo di attività hai?",
    hint: "Scegli la categoria più vicina alla tua realtà.",
    options: [
      { v: "Ristorazione / F&B", w: { onda: 1, tsunami: 2, oceano: 1 } },
      { v: "Retail / Negozio fisico", w: { onda: 2, tsunami: 2, oceano: 0 } },
      { v: "Servizi professionali", w: { onda: 1, tsunami: 2, oceano: 1 } },
      { v: "Beauty / Wellness", w: { onda: 2, tsunami: 1, oceano: 1 } },
      { v: "Artista / Personal brand", w: { onda: 1, tsunami: 2, oceano: 2 } },
      { v: "Altro", w: { onda: 1, tsunami: 1, oceano: 1 } },
    ],
  },
  {
    key: "goal",
    title: "Qual è l'obiettivo principale?",
    hint: "Puoi selezionarne solo uno: il più importante per te ora.",
    options: [
      { v: "Aumentare i follower", w: { onda: 2, tsunami: 2, oceano: 1 } },
      { v: "Generare prenotazioni / vendite", w: { onda: 0, tsunami: 2, oceano: 2 } },
      { v: "Rifare l'identità del brand", w: { onda: 0, tsunami: 1, oceano: 3 } },
      { v: "Lanciare un evento / apertura", w: { onda: 0, tsunami: 1, oceano: 3 } },
      { v: "Essere trovato su Google", w: { onda: 1, tsunami: 1, oceano: 2 } },
    ],
  },
  {
    key: "budget",
    title: "Che budget mensile puoi investire?",
    hint: "Comprende il compenso agenzia (l'ADV a Meta è extra).",
    options: [
      { v: "Fino a 700€", w: { onda: 3, tsunami: 0, oceano: 0 } },
      { v: "700€ – 1.500€", w: { onda: 1, tsunami: 3, oceano: 0 } },
      { v: "1.500€ – 3.000€", w: { onda: 0, tsunami: 1, oceano: 3 } },
      { v: "Oltre 3.000€", w: { onda: 0, tsunami: 0, oceano: 4 } },
    ],
  },
  {
    key: "presence",
    title: "Come descriveresti la tua presenza social oggi?",
    hint: "Sii sincero: ci aiuta a calibrare la proposta.",
    options: [
      { v: "Non esistiamo ancora", w: { onda: 1, tsunami: 2, oceano: 2 } },
      { v: "Abbiamo un profilo ma non lo curiamo", w: { onda: 2, tsunami: 2, oceano: 1 } },
      { v: "Pubblichiamo saltuariamente", w: { onda: 2, tsunami: 2, oceano: 1 } },
      { v: "Siamo attivi ma senza strategia", w: { onda: 0, tsunami: 2, oceano: 2 } },
      { v: "Vogliamo un salto di qualità premium", w: { onda: 0, tsunami: 1, oceano: 3 } },
    ],
  },
];

function computeTier(answers) {
  const totals = { onda: 0, tsunami: 0, oceano: 0 };
  for (const step of STEPS) {
    const val = answers[step.key];
    const opt = step.options.find((o) => o.v === val);
    if (!opt) continue;
    for (const k of Object.keys(totals)) totals[k] += opt.w[k] || 0;
  }
  let best = "tsunami";
  let bestScore = -1;
  for (const k of Object.keys(totals)) {
    if (totals[k] > bestScore) { best = k; bestScore = totals[k]; }
  }
  return { tier: best, totals };
}

const TIER_META = {
  onda: {
    label: "L'Onda",
    price: "€650 / mese",
    tagline: "Il punto di partenza ideale per costruire una presenza professionale.",
    accent: "from-purple-500/25 to-blue-500/15",
  },
  tsunami: {
    label: "Lo Tsunami",
    price: "€1.100 / mese",
    tagline: "Il piano più scelto: contenuti, ADV e report per crescere davvero.",
    accent: "from-purple-500/40 to-blue-500/30",
  },
  oceano: {
    label: "L'Oceano",
    price: "€2.500 / mese",
    tagline: "Il piano premium per chi vuole dominare il territorio online e offline.",
    accent: "from-purple-500/45 to-blue-500/40",
  },
};

export default function Quiz() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);
  const [contact, setContact] = useState({ name: "", email: "", phone: "", note: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const current = STEPS[step];
  const selected = answers[current?.key];
  const isLast = step === STEPS.length - 1;

  const pick = (v) => setAnswers((s) => ({ ...s, [current.key]: v }));
  const next = () => {
    if (!selected) return;
    if (isLast) setShowResult(true);
    else setStep(step + 1);
  };
  const prev = () => setStep(Math.max(0, step - 1));

  const result = showResult ? computeTier(answers) : null;
  const tierMeta = result ? TIER_META[result.tier] : null;

  const submit = async (e) => {
    e.preventDefault();
    if (!contact.name.trim() || !contact.email.trim()) {
      toast.error("Compila nome ed email per ricevere la proposta.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/quiz/submit`, {
        name: contact.name,
        email: contact.email,
        phone: contact.phone || undefined,
        note: contact.note || undefined,
        recommended_tier: result.tier,
        answers: {
          business_type: answers.business_type,
          goal: answers.goal,
          budget: answers.budget,
          presence: answers.presence,
        },
      });
      setSent(true);
      toast.success("Perfetto! Ti abbiamo inviato la proposta via email.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Invio non riuscito. Riprova.");
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setStep(0);
    setAnswers({});
    setShowResult(false);
    setContact({ name: "", email: "", phone: "", note: "" });
    setSent(false);
  };

  return (
    <section id="quiz" data-testid="section-quiz" className="relative py-16 md:py-24">
      <div className="max-w-5xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl mb-12">
          <div className="text-xs uppercase tracking-[0.3em] text-purple-300/80 mb-4">06 · Quiz</div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter text-white leading-[0.95]">
            Che pacchetto <br />fa per <span className="text-gradient">te</span>?
          </h2>
          <p className="mt-6 text-slate-400 text-base md:text-lg">
            Rispondi a 4 domande in meno di un minuto. Ti diciamo quale piano è tagliato sulla tua
            realtà e ti inviamo la proposta via email.
          </p>
        </div>

        <div className="glass gradient-border rounded-3xl p-6 md:p-10 relative overflow-hidden">
          {/* Progress */}
          {!showResult && (
            <div className="mb-8 flex items-center gap-3">
              <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-purple-500 to-blue-500"
                  animate={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
              <div className="text-xs text-slate-400 tabular-nums font-medium">
                {step + 1} / {STEPS.length}
              </div>
            </div>
          )}

          <AnimatePresence mode="wait">
            {!showResult ? (
              <motion.div
                key={`step-${step}`}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.35 }}
              >
                <h3 data-testid={`quiz-step-${step}-title`} className="font-display text-2xl md:text-3xl text-white leading-tight">
                  {current.title}
                </h3>
                <p className="mt-2 text-sm text-slate-400">{current.hint}</p>

                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {current.options.map((o) => {
                    const active = selected === o.v;
                    return (
                      <button
                        type="button"
                        key={o.v}
                        onClick={() => pick(o.v)}
                        data-testid={`quiz-option-${step}-${o.v.slice(0, 20).replace(/\s+/g, "-")}`}
                        className={`group text-left rounded-2xl border px-5 py-4 transition-all ${
                          active
                            ? "bg-gradient-to-br from-purple-500/25 to-blue-500/20 border-purple-400/60 text-white shadow-[0_0_25px_rgba(147,51,234,0.35)]"
                            : "bg-white/[0.02] border-white/10 text-slate-200 hover:border-white/25 hover:bg-white/[0.05]"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-4">
                          <span className="text-sm md:text-base">{o.v}</span>
                          <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full border ${active ? "bg-white text-purple-700 border-white" : "border-white/20 text-transparent"}`}>
                            <Check size={12} strokeWidth={3} />
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>

                <div className="mt-10 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={prev}
                    disabled={step === 0}
                    data-testid="quiz-prev"
                    className="btn-ghost disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ArrowLeft size={16} /> Indietro
                  </button>
                  <button
                    type="button"
                    onClick={next}
                    disabled={!selected}
                    data-testid="quiz-next"
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLast ? "Vedi il risultato" : "Avanti"} <ArrowRight size={16} />
                  </button>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="result"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                data-testid="quiz-result"
              >
                <div className={`rounded-2xl p-6 md:p-8 border border-white/10 bg-gradient-to-br ${tierMeta.accent}`}>
                  <div className="flex items-center gap-2 text-purple-200 text-xs uppercase tracking-[0.25em]">
                    <Sparkles size={14} /> Il pacchetto giusto per te
                  </div>
                  <div className="mt-3 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                    <div>
                      <h3 className="font-display text-4xl md:text-5xl text-white leading-none">{tierMeta.label}</h3>
                      <p className="mt-2 text-slate-200 max-w-lg">{tierMeta.tagline}</p>
                    </div>
                    <div className="text-white font-display text-2xl md:text-3xl">{tierMeta.price}</div>
                  </div>
                </div>

                {sent ? (
                  <div className="mt-8 rounded-2xl p-6 border border-purple-400/40 bg-white/[0.03] text-center">
                    <div className="text-white font-display text-2xl">Grazie {contact.name.split(" ")[0] || ""} 🐳</div>
                    <p className="mt-2 text-slate-300 text-sm">
                      Abbiamo ricevuto il tuo profilo e ti invieremo la proposta via email a breve.
                    </p>
                    <button onClick={reset} data-testid="quiz-restart" className="btn-ghost mt-6">
                      Rifai il quiz
                    </button>
                  </div>
                ) : (
                  <form onSubmit={submit} className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="quiz-lead-form">
                    <input
                      required
                      placeholder="Nome e cognome"
                      value={contact.name}
                      onChange={(e) => setContact((c) => ({ ...c, name: e.target.value }))}
                      data-testid="quiz-input-name"
                      className="bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-slate-500 focus:border-purple-400/50 outline-none transition"
                    />
                    <input
                      required
                      type="email"
                      placeholder="Email"
                      value={contact.email}
                      onChange={(e) => setContact((c) => ({ ...c, email: e.target.value }))}
                      data-testid="quiz-input-email"
                      className="bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-slate-500 focus:border-purple-400/50 outline-none transition"
                    />
                    <input
                      placeholder="Telefono (opzionale)"
                      value={contact.phone}
                      onChange={(e) => setContact((c) => ({ ...c, phone: e.target.value }))}
                      data-testid="quiz-input-phone"
                      className="bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-slate-500 focus:border-purple-400/50 outline-none transition md:col-span-2"
                    />
                    <textarea
                      rows={3}
                      placeholder="Aggiungi qualcosa sul tuo brand (opzionale)"
                      value={contact.note}
                      onChange={(e) => setContact((c) => ({ ...c, note: e.target.value }))}
                      data-testid="quiz-input-note"
                      className="md:col-span-2 bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-slate-500 focus:border-purple-400/50 outline-none transition resize-none"
                    />
                    <div className="md:col-span-2 flex flex-col md:flex-row md:items-center justify-between gap-4 mt-2">
                      <button type="button" onClick={reset} className="btn-ghost" data-testid="quiz-restart-inline">
                        <ArrowLeft size={14} /> Rifai il quiz
                      </button>
                      <button type="submit" disabled={sending} className="btn-primary disabled:opacity-70" data-testid="quiz-submit">
                        {sending ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} />}
                        {sending ? "Invio…" : "Ricevi la proposta"}
                      </button>
                    </div>
                  </form>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
